import { skipToken } from '@reduxjs/toolkit/query';
import { capitalizeString } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { FormHeaderAndRowContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { useNavigate, useParams } from 'react-router';

import { BidDetails } from 'components/liveReferrals/BidDetails';
import { OffHoursGuard } from 'components/liveReferrals/OffHoursGuard';
import { PatientDetails } from 'components/liveReferrals/PatientDetails';
import { ReferralDetails } from 'components/liveReferrals/ReferralDetails';
import { HealthPlanNotes } from 'components/shared/HealthPlanNotes';
import { MultiStrengthTooltip } from 'components/shared/MultiStrengthTooltip';
import { Performance } from 'components/shared/Performance';
import { ProductLabel } from 'components/shared/ProductLabel';
import { useReferralDetails } from 'services/APIHooks';
import { PayerFeatures, usePayerFeatures } from 'services/FeatureFlags';
import {
    addQtyUnits,
    getEstDispensedQtyUnits,
    useProductConfig,
} from 'services/ProductConfigHelpers';
import { AppRoutes, useCurrentReferralLocation } from 'services/routes';
import { useGetPlaceOfServiceMapQuery, useGetPrescriberDetailsQuery } from 'store/api/referenceApi';
import { useLiveReferralData } from 'store/liveReferrals/liveReferralsHooks';

// eslint-disable-next-line complexity
export const LiveReferralDetailsPage = () => {
    const { referralId } = useParams() as { referralId: string };
    const navigate = useNavigate();

    const { liveReferralData } = useLiveReferralData();
    const selectedReferral = liveReferralData.find(
        (referral) => referral.referralId === referralId,
    );

    const referralDetails = useReferralDetails(
        selectedReferral?.referralId,
        selectedReferral?.payerId,
    );

    // Navigate back to Live Referrals list if referralId is invalid
    useCurrentReferralLocation(AppRoutes.liveReferrals);

    const { data: prescriberDetails } = useGetPrescriberDetailsQuery(
        selectedReferral?.prescriberId ? { npi: selectedReferral.prescriberId } : skipToken,
    );
    const payerFeatures = usePayerFeatures(selectedReferral?.payerId);
    const productConfig = useProductConfig({
        segmentId: selectedReferral?.segmentId,
        productServiceOid: selectedReferral?.productServiceOid,
        benefit: selectedReferral?.benefit,
    });
    const { data: placeOfServiceMap } = useGetPlaceOfServiceMapQuery();

    return (
        <OffHoursGuard>
            <FullscreenLoading
                isShowing={!selectedReferral}
                loadingText="Loading Live Referral information..."
            />
            <FormHeaderAndRowContent
                rowOneContentHeader={
                    <ActionButtonPageTitle
                        label="Live Referrals"
                        title="Live Referral Details"
                        id="live-referral-details-page-title-button"
                        onClick={() => navigate(AppRoutes.liveReferrals)}
                    />
                }
                rowTwoContentHeader={
                    selectedReferral && (
                        <>
                            <FormTitleLabelValuePair
                                label={
                                    <ProductLabel
                                        product={selectedReferral.product}
                                        isBrand={selectedReferral.isBrand}
                                    />
                                }
                                value={selectedReferral.strength}
                            />
                            <HeaderLabelValuePair
                                label="Estimated Quantity"
                                value={
                                    selectedReferral.qty ? (
                                        <span
                                            className="flex__gap--x-small"
                                            style={{ alignItems: 'center' }}
                                        >
                                            <span>
                                                {addQtyUnits(
                                                    selectedReferral.qty,
                                                    getEstDispensedQtyUnits(productConfig),
                                                )}
                                            </span>
                                            {productConfig?.multistrengthFlag && (
                                                <MultiStrengthTooltip />
                                            )}
                                        </span>
                                    ) : undefined
                                }
                            />
                            <HeaderLabelValuePair
                                label="Refills"
                                value={selectedReferral.refillCount?.toLocaleString()}
                            />
                            <HeaderLabelValuePair
                                label="Health Plan"
                                value={selectedReferral.segmentName}
                            />
                            <HeaderLabelValuePair
                                label="Place Of Service"
                                value={placeOfServiceMap?.[selectedReferral.placeOfService]}
                            />
                            <HeaderLabelValuePair
                                label="Billing"
                                value={capitalizeString(selectedReferral.benefit)}
                            />
                        </>
                    )
                }
                rowOneContent={
                    selectedReferral && (
                        <>
                            <BidDetails
                                selectedReferral={selectedReferral}
                                referralDetails={referralDetails}
                            />
                            <div
                                className="flex__gap--medium"
                                style={{ width: '60%', flexDirection: 'column' }}
                            >
                                <PatientDetails
                                    selectedReferral={selectedReferral}
                                    prescriberDetails={prescriberDetails}
                                />
                                <ReferralDetails
                                    selectedReferral={selectedReferral}
                                    referralDetails={referralDetails}
                                />
                            </div>
                        </>
                    )
                }
                rowTwoContent={
                    (selectedReferral?.valueCases ?? []).length > 0 && (
                        <Performance selectedReferral={selectedReferral} />
                    )
                }
                rowThreeContent={
                    payerFeatures?.[PayerFeatures.PLAN_NOTES] && (
                        <HealthPlanNotes payerId={selectedReferral?.payerId} />
                    )
                }
            />
        </OffHoursGuard>
    );
};
